import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ElementRef, HostListener, OnInit, AfterViewInit } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { AccessibilityFocusService } from 'src/app/services/accessibility.service';

export enum TOGGLE_TAG {
	All = 'all',
	Vegeterian = 'veggie',
	Vegan = 'vegan',
	Fish = 'fish',
	Meat = 'meat',
	Alcohol = 'alcohol',
}

interface FilterOption {
	tag: TOGGLE_TAG;
	label: string;
	icon: string;
	ariaLabel: string;
}

@Component({
	selector: 'app-menu-filters',
	standalone: true,
	imports: [CommonModule, IonicModule],
	template: `
		<!-- Accessible menu filter component -->
		<div class="vegan_toggle" role="region" aria-labelledby="filter-heading">
			<!-- Screen reader heading -->
			<h3 id="filter-heading" class="visually-hidden">Food category filters</h3>

			<div class="toggler" role="tablist" aria-label="Food category selection">
				<!-- Filter options -->
				<button
					*ngFor="let option of filterOptions; let i = index"
					class="toggler_item"
					[class.all-item]="option.tag === toggleTagEnum.All"
					[class.image-block]="option.tag !== toggleTagEnum.All"
					[class.selected]="selectedToggleTag === option.tag"
					[attr.id]="'filter-' + option.tag"
					[attr.aria-label]="option.ariaLabel"
					[attr.aria-selected]="selectedToggleTag === option.tag ? 'true' : 'false'"
					[attr.tabindex]="selectedToggleTag === option.tag ? '0' : '-1'"
					role="tab"
					(click)="toggleSwitch(option.tag)"
					(keydown)="handleKeyNavigation($event, i)"
				>
					<ion-icon
						[src]="option.icon"
						[class.first-img]="option.tag === toggleTagEnum.Vegan"
						[class.second-img]="option.tag === toggleTagEnum.Vegeterian"
						aria-hidden="true"
					>
					</ion-icon>

					<p aria-hidden="true">{{ option.label }}</p>

					<!-- Screen reader text -->
					<span class="visually-hidden">
						{{ option.ariaLabel }}
						{{ selectedToggleTag === option.tag ? ', currently selected' : '' }}
					</span>
				</button>

				<!-- Visual indicator pin -->
				<div
					class="toggler_pin"
					[ngClass]="{
						first: selectedToggleTag === toggleTagEnum.All,
						second: selectedToggleTag === toggleTagEnum.Vegeterian,
						third: selectedToggleTag === toggleTagEnum.Vegan,
					}"
					aria-hidden="true"
				></div>
			</div>

			<!-- Live region for announcements -->
			<div class="visually-hidden" aria-live="polite" id="filter-announcer"></div>

			<!-- Instructions for keyboard users -->
			<div class="visually-hidden" id="filter-instructions">
				Use left and right arrow keys to navigate between food categories. Press Enter or Space to select.
			</div>
		</div>
	`,
	styleUrls: ['./menu-filters.component.scss'],
})
export class MenuFiltersComponent implements OnInit, AfterViewInit {
	swipeCoord = [0, 0];
	swipeTime = new Date().getTime();

	@Input() selectedToggleTag: TOGGLE_TAG = TOGGLE_TAG.All;
	@Input() showFilters = false;
	@Output() showFiltersValue = new EventEmitter<boolean>();
	@Output() toggleTagValue = new EventEmitter<TOGGLE_TAG>();

	toggleTagEnum = TOGGLE_TAG;

	// Current focus index for keyboard navigation
	private currentFocusIndex = 0;

	// Filter options configuration
	filterOptions: FilterOption[] = [
		{
			tag: TOGGLE_TAG.All,
			label: 'All',
			icon: '../../../assets/dean_and_david/slider-veggie/all.svg',
			ariaLabel: 'Show all food items',
		},
		{
			tag: TOGGLE_TAG.Vegeterian,
			label: 'Veggie',
			icon: '../../../assets/dean_and_david/slider-veggie/veggie.svg',
			ariaLabel: 'Show vegetarian food items only',
		},
		{
			tag: TOGGLE_TAG.Vegan,
			label: 'Vegan',
			icon: '../../../assets/dean_and_david/slider-veggie/vegetarian.svg',
			ariaLabel: 'Show vegan food items only',
		},
	];

	constructor(
		private elementRef: ElementRef,
		private a11yService: AccessibilityFocusService
	) {}

	ngOnInit() {
		// Set initial focus index based on selected tag
		this.updateFocusIndex();
	}

	ngAfterViewInit() {
		// Set initial focus and announce component
		setTimeout(() => {
			this.setFocusToSelected();
			this.a11yService.announceToScreenReader('Food category filters loaded');
		}, 100);
	}

	/**
	 * Toggle filter selection with accessibility enhancements
	 */
	toggleSwitch(selectedItem: TOGGLE_TAG): void {
		const previousSelection = this.selectedToggleTag;
		this.selectedToggleTag = selectedItem;
		this.toggleTagValue.emit(selectedItem);

		// Update focus index
		this.updateFocusIndex();

		// Get selected option for announcement
		const selectedOption = this.filterOptions.find(option => option.tag === selectedItem);

		if (selectedOption && previousSelection !== selectedItem) {
			// Announce selection change
			this.a11yService.announceToScreenReader(`${selectedOption.ariaLabel} selected`);

			// Update announcer element
			this.updateFilterAnnouncer(`Filter changed to ${selectedOption.label}`);
		}
	}

	/**
	 * Show/hide additional filters
	 */
	showFiltersFunc(): void {
		this.showFilters = !this.showFilters;
		this.showFiltersValue.emit(this.showFilters);

		// Announce state change
		const message = this.showFilters ? 'Additional filters shown' : 'Additional filters hidden';
		this.a11yService.announceToScreenReader(message);
	}

	/**
	 * Handle keyboard navigation
	 */
	handleKeyNavigation(event: KeyboardEvent, currentIndex: number): void {
		let newIndex = currentIndex;

		switch (event.key) {
			case 'ArrowLeft':
				event.preventDefault();
				newIndex = currentIndex > 0 ? currentIndex - 1 : this.filterOptions.length - 1;
				this.focusOption(newIndex);
				break;

			case 'ArrowRight':
				event.preventDefault();
				newIndex = currentIndex < this.filterOptions.length - 1 ? currentIndex + 1 : 0;
				this.focusOption(newIndex);
				break;

			case 'Home':
				event.preventDefault();
				newIndex = 0;
				this.focusOption(newIndex);
				break;

			case 'End':
				event.preventDefault();
				newIndex = this.filterOptions.length - 1;
				this.focusOption(newIndex);
				break;

			case 'Enter':
			case ' ':
				event.preventDefault();
				if (this.filterOptions[currentIndex]) {
					this.toggleSwitch(this.filterOptions[currentIndex].tag);
				}
				break;
		}

		this.currentFocusIndex = newIndex;
	}

	/**
	 * Global keyboard event handler
	 */
	@HostListener('keydown', ['$event'])
	handleGlobalKeydown(event: KeyboardEvent): void {
		// Handle global shortcuts if needed
		if (event.key === 'Escape') {
			// Could be used to close additional filters if open
			if (this.showFilters) {
				this.showFiltersFunc();
			}
		}
	}

	/**
	 * Focus a specific filter option
	 */
	private focusOption(index: number): void {
		const option = this.filterOptions[index];
		if (option) {
			const button = this.elementRef.nativeElement.querySelector(`#filter-${option.tag}`);
			if (button) {
				// Update tabindex for all buttons
				this.updateTabIndices(index);
				// Focus the button
				button.focus();
			}
		}
	}

	/**
	 * Set focus to currently selected option
	 */
	private setFocusToSelected(): void {
		const selectedIndex = this.filterOptions.findIndex(option => option.tag === this.selectedToggleTag);

		if (selectedIndex >= 0) {
			this.updateTabIndices(selectedIndex);
		}
	}

	/**
	 * Update focus index based on selected tag
	 */
	private updateFocusIndex(): void {
		this.currentFocusIndex = this.filterOptions.findIndex(option => option.tag === this.selectedToggleTag);

		if (this.currentFocusIndex < 0) {
			this.currentFocusIndex = 0;
		}
	}

	/**
	 * Update tabindex attributes for proper tab navigation
	 */
	private updateTabIndices(focusedIndex: number): void {
		this.filterOptions.forEach((option, index) => {
			const button = this.elementRef.nativeElement.querySelector(`#filter-${option.tag}`);
			if (button) {
				button.setAttribute('tabindex', index === focusedIndex ? '0' : '-1');
				button.setAttribute('aria-selected', option.tag === this.selectedToggleTag ? 'true' : 'false');
			}
		});
	}

	/**
	 * Update filter announcer for screen readers
	 */
	private updateFilterAnnouncer(message: string): void {
		const announcer = this.elementRef.nativeElement.querySelector('#filter-announcer');
		if (announcer) {
			announcer.textContent = message;

			// Clear after announcement
			setTimeout(() => {
				announcer.textContent = '';
			}, 3000);
		}
	}

	/**
	 * Get current selection for external access
	 */
	getCurrentSelection(): TOGGLE_TAG {
		return this.selectedToggleTag;
	}

	/**
	 * Get filter option by tag
	 */
	getFilterOption(tag: TOGGLE_TAG): FilterOption | undefined {
		return this.filterOptions.find(option => option.tag === tag);
	}
}
